import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Clock, Check, X } from "lucide-react";

/**
 * Iter51 · Fixture Reschedule Wizard.
 *
 * Two shapes:
 * - button: rep opens dialog, picks new time + reason, submits proposal
 * - decision-pill: opponent side sees pending proposal + accept/decline
 *
 * Preserves round_number — the round grouping stays intact.
 */
export function ReschedulePropose({ fixture, onDone }) {
  const [open, setOpen] = useState(false);
  const [newAt, setNewAt] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!newAt) { toast.error("Pick a new time."); return; }
    setBusy(true);
    try {
      await api.post(`/fixtures/${fixture.id}/reschedule-propose`, {
        new_scheduled_at: new Date(newAt).toISOString(),
        reason: reason.trim() || null,
      });
      toast.success("Reschedule request sent — the other rep gets a Discord ping.");
      setOpen(false);
      setNewAt("");
      setReason("");
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const pending = fixture.reschedule_proposal?.status === "pending";
  if (pending) return null;  // decision pill takes over

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid={`reschedule-open-${fixture.id}`}
        className="text-[10px] font-mono uppercase tracking-widest text-[#B084FF] border border-[#B084FF] hover:bg-[#B084FF] hover:text-black px-3 py-1.5 flex items-center gap-1 font-bold"
        title="Propose a new time for this fixture — round grouping stays intact"
      >
        <Clock className="w-3 h-3" /> Reschedule
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#B084FF]" /> Reschedule request
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
              Round {fixture.round_number || "?"} · The round number stays the same — only the kickoff moves. The other rep will approve on Discord.
            </p>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">New kickoff (UTC)</Label>
              <Input
                data-testid={`reschedule-new-at-${fixture.id}`}
                type="datetime-local"
                value={newAt}
                onChange={(e) => setNewAt(e.target.value)}
                className="rounded-none bg-[#05050A] border-[#232330] mt-2 font-mono text-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">Reason (optional)</Label>
              <Input
                data-testid={`reschedule-reason-${fixture.id}`}
                maxLength={280}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Scrim conflict, timezone, etc."
                className="rounded-none bg-[#05050A] border-[#232330] mt-2 font-mono text-sm"
              />
            </div>
            <Button
              onClick={submit}
              disabled={busy || !newAt}
              data-testid={`reschedule-submit-${fixture.id}`}
              className="w-full rounded-none bg-[#B084FF] hover:bg-purple-400 text-black uppercase tracking-widest font-bold disabled:opacity-50"
            >
              <Clock className="w-4 h-4 mr-2" /> {busy ? "Sending…" : "Send request"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function RescheduleDecision({ fixture, onDone }) {
  const [busy, setBusy] = useState(false);
  const p = fixture.reschedule_proposal;
  if (!p || p.status !== "pending") return null;

  const respond = async (accept) => {
    setBusy(true);
    try {
      await api.post(`/fixtures/${fixture.id}/reschedule-decision`, { accept });
      toast.success(accept ? "Reschedule accepted — kickoff moved." : "Reschedule declined.");
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-[#B084FF] bg-[#B084FF]/10 p-3 space-y-2" data-testid={`reschedule-pending-${fixture.id}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#B084FF]">/ RESCHEDULE REQUEST</div>
      <div className="text-sm">
        Proposed new kickoff: <span className="font-mono text-[#F4D03F]">{new Date(p.new_scheduled_at).toLocaleString()}</span>
      </div>
      {p.reason && <div className="text-xs text-gray-400 italic">"{p.reason}"</div>}
      <div className="flex gap-2 pt-1">
        <button
          onClick={() => respond(true)}
          disabled={busy}
          data-testid={`reschedule-accept-${fixture.id}`}
          className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 border border-cyan-400 hover:bg-cyan-400 hover:text-black px-3 py-1.5 flex items-center gap-1 font-bold disabled:opacity-50"
        >
          <Check className="w-3 h-3" /> Accept
        </button>
        <button
          onClick={() => respond(false)}
          disabled={busy}
          data-testid={`reschedule-decline-${fixture.id}`}
          className="text-[10px] font-mono uppercase tracking-widest text-red-500 border border-red-500 hover:bg-red-500 hover:text-white px-3 py-1.5 flex items-center gap-1 font-bold disabled:opacity-50"
        >
          <X className="w-3 h-3" /> Decline
        </button>
      </div>
    </div>
  );
}
